import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { ContractIcon, SummaryIcon } from "@/assets/icons";
import LoadingWrapper from "@/components/ui/LoadingWrapper";
import { MESSAGE } from "@/constants/message";
import {
  ExecutiveSummaryFeedback,
  useExecutiveSummary
} from "@/hooks/analysisSummary/useExecutiveSummary";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";

import * as Tabs from "@radix-ui/react-tabs";

import { AISummary } from "./AISummary";
import { ReportSummary } from "./ReportSummary";

type AnalysisSummaryWrapperProps = {
  isExpanded: boolean;
  isFetching?: boolean;
  chartRef: React.RefObject<HTMLDivElement>;
  dynamicChartRef: React.RefObject<(HTMLDivElement | null)[]>;
  onSummaryComplete: (summary: string) => void;
  onCollapse?: () => void;
  permissionAI: boolean;
};

export const AnalysisSummaryWrapper = ({
  isExpanded,
  isFetching = false,
  chartRef,
  dynamicChartRef,
  onSummaryComplete,
  onCollapse,
  permissionAI
}: AnalysisSummaryWrapperProps) => {
  const { isAISummaryEnabled } = useAppFeatures();
  const { latestAnalysisId } = useSelector((state: RootState) => state.spatial);
  const [chunks, setChunks] = useState<string[]>([]);
  const [isStreaming, setIsStreaming] = useState(true);
  const [initialFeedback, setInitialFeedback] =
    useState<ExecutiveSummaryFeedback | null>(null);
  const { isHasSummary } = useSelector(
    (state: RootState) => state.analysisSummary
  );

  useEffect(() => {
    setChunks([]);
    setIsStreaming(true);
    setInitialFeedback(null);
  }, [latestAnalysisId]);

  useExecutiveSummary({
    analysisId:
      isAISummaryEnabled && permissionAI ? latestAnalysisId : undefined,
    onDelta: (chunk) => {
      setChunks((prev) => [...prev, chunk.content]);
    },
    onFinal: (full) => {
      setChunks([full.content]);
      setIsStreaming(false);
      onSummaryComplete(full.content);
    },
    onDone: (done) => {
      setInitialFeedback(done.feedback ?? null);
      setIsStreaming(false);
    }
  });

  if (isFetching) {
    return (
      <LoadingWrapper
        isLoading
        loadingClassName="h-[300px]"
        loadingText={
          <span className="ml-2 text-sm text-txtBody">Loading summary...</span>
        }
      />
    );
  }

  if (!isAISummaryEnabled) {
    return (
      <ReportSummary
        className="flex overflow-auto my-4 mx-5 custom-scrollbar pr-2"
        chartRef={chartRef}
        dynamicChartRef={dynamicChartRef}
        isExpanded={isExpanded}
      />
    );
  }

  // Render no summary message when isHasSummary is false
  if (!isHasSummary && permissionAI) {
    return (
      <Tabs.Root
        defaultValue="report"
        className="relative overflow-auto custom-scrollbar my-4 pr-2"
      >
        <Tabs.List className="grid grid-cols-2 gap-4 border-b border-[#E5E7EB] mb-4 sticky top-0 z-10 bg-white">
          <Tabs.Trigger
            value="report"
            className="group px-4 py-2 text-sm data-[state=active]:border-b-2 data-[state=active]:border-primary-teal data-[state=active]:text-primary-teal data-[state=active]:font-semibold outline-none"
          >
            <div className="flex gap-1 items-center justify-center">
              <ContractIcon className="fill-txtBody group-data-[state=active]:fill-primary-teal" />
              Report Summary
            </div>
          </Tabs.Trigger>

          <Tabs.Trigger
            value="ai"
            className="group px-4 py-2 text-sm data-[state=active]:border-b-2 data-[state=active]:border-primary-teal data-[state=active]:text-primary-teal data-[state=active]:font-semibold outline-none"
          >
            <div className="flex gap-1 items-center justify-center">
              <SummaryIcon className="fill-txtBody group-data-[state=active]:fill-primary-teal" />
              AI Summary
            </div>
          </Tabs.Trigger>
        </Tabs.List>

        {/* Report Summary Tab */}
        <Tabs.Content
          value="report"
          forceMount
          className="data-[state=inactive]:absolute data-[state=inactive]:-left-[9999px] data-[state=inactive]:top-0 data-[state=inactive]:w-full data-[state=inactive]:pointer-events-none"
        >
          <ReportSummary
            className="flex mx-5"
            chartRef={chartRef}
            dynamicChartRef={dynamicChartRef}
            isExpanded={isExpanded}
          />
        </Tabs.Content>

        {/* AI Summary Tab - No Summary Message */}
        <Tabs.Content
          value="ai"
          forceMount
          className="data-[state=inactive]:absolute data-[state=inactive]:-left-[9999px] data-[state=inactive]:top-0 data-[state=inactive]:w-full data-[state=inactive]:pointer-events-none"
        >
          <div className="mx-5">
            <div className="mt-3 bg-white-smoke flex-col w-full items-center justify-between text-center p-4 rounded-xl print:mt-4">
              <p className="text-txtBody text-sm font-semibold">
                {MESSAGE.AI_SUMMARY_NO_SCORES_AVAILABLE_TITLE}
              </p>
              <p className="text-txtBody text-xs">
                {MESSAGE.AI_SUMMARY_NO_SCORES_AVAILABLE_DESCRIPTION}
              </p>
            </div>
          </div>
        </Tabs.Content>
      </Tabs.Root>
    );
  }

  // Render tabs with AI Summary when isHasSummary is true
  return (
    <Tabs.Root
      defaultValue="report"
      className="relative overflow-auto custom-scrollbar my-4 pr-2"
    >
      <Tabs.List className="grid grid-cols-2 gap-4 border-b border-[#E5E7EB] mb-4 sticky top-0 z-10 bg-white">
        <Tabs.Trigger
          value="report"
          className="group px-4 py-2 text-sm data-[state=active]:border-b-2 data-[state=active]:border-primary-teal data-[state=active]:text-primary-teal data-[state=active]:font-semibold outline-none"
        >
          <div className="flex gap-1 items-center justify-center">
            <ContractIcon className="fill-txtBody group-data-[state=active]:fill-primary-teal" />
            Report Summary
          </div>
        </Tabs.Trigger>

        <Tabs.Trigger
          value="ai"
          className="group px-4 py-2 text-sm data-[state=active]:border-b-2 data-[state=active]:border-primary-teal data-[state=active]:text-primary-teal data-[state=active]:font-semibold outline-none"
        >
          <div className="flex gap-1 items-center justify-center">
            <SummaryIcon className="fill-txtBody group-data-[state=active]:fill-primary-teal" />
            AI Summary
          </div>
        </Tabs.Trigger>
      </Tabs.List>

      {/* Report Summary Tab */}
      <Tabs.Content
        value="report"
        forceMount
        className="data-[state=inactive]:absolute data-[state=inactive]:-left-[9999px] data-[state=inactive]:top-0 data-[state=inactive]:w-full data-[state=inactive]:pointer-events-none"
      >
        <ReportSummary
          className="flex mx-5"
          chartRef={chartRef}
          dynamicChartRef={dynamicChartRef}
          isExpanded={isExpanded}
        />
      </Tabs.Content>

      {/* AI Summary Tab */}
      <Tabs.Content
        value="ai"
        forceMount
        className="data-[state=inactive]:absolute data-[state=inactive]:-left-[9999px] data-[state=inactive]:top-0 data-[state=inactive]:w-full data-[state=inactive]:pointer-events-none"
      >
        <AISummary
          chunks={chunks}
          isStreaming={isStreaming}
          permissionAI={permissionAI}
          isExpanded={isExpanded}
          onCollapse={onCollapse}
          initialFeedback={initialFeedback}
        />
      </Tabs.Content>
    </Tabs.Root>
  );
};
